import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
  type Method,
} from "axios";
import {
  clearTokens,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
} from "@/lib/authToken";
import { url as appUrl } from "@/lib/basePath";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
    public errorCode?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface IdResponse {
  id: string;
}

export interface OperationResult {
  success: boolean;
  message: string | null;
}

// VITE_API_BASE_URL is the API origin (may be empty for same-origin). The
// `/api/v1` version prefix is centralized here, so feature modules use
// version-agnostic relative paths like `/merchants` or `/quotes/${id}`.
const API_BASE_URL = (
  (typeof window !== "undefined" && window.__ENV__?.VITE_API_BASE_URL) ||
  import.meta.env.VITE_API_BASE_URL ||
  ""
).replace(/\/+$/, "");
const V1 = API_BASE_URL ? `${API_BASE_URL}/api/v1` : appUrl("api/v1");

// Fired on any 401 so the AuthProvider can drop the session and bounce to
// /login. Dispatched from the interceptor to avoid importing the router here.
export const UNAUTHORIZED_EVENT = "blogic:unauthorized";

export const api = axios.create({ baseURL: V1 });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

interface RefreshResponse {
  token: string;
  refreshToken?: string;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout"];

function isNoRefreshPath(url?: string): boolean {
  return !!url && NO_REFRESH_PATHS.some((p) => url.includes(p));
}

const NO_SIGNOUT_PATHS = ["/health"];

function isNoSignoutPath(url?: string): boolean {
  return !!url && NO_SIGNOUT_PATHS.some((p) => url.endsWith(p));
}

let refreshPromise: Promise<void> | null = null;

async function performRefresh(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("no refresh token");
  const res = await axios.post(`${V1}/auth/refresh`, { refreshToken });
  const data = unwrap<RefreshResponse>(res.data);
  if (!data?.token) throw new Error("refresh response missing token");
  setToken(data.token);
  if (data.refreshToken) setRefreshToken(data.refreshToken);
}

function ensureRefreshed(): Promise<void> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function signalUnauthorized(): void {
  clearTokens();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as RetriableConfig | undefined;

    if (isNoSignoutPath(config?.url)) return Promise.reject(error);

    if (
      status === 401 &&
      config &&
      !config._retry &&
      !isNoRefreshPath(config.url) &&
      getRefreshToken()
    ) {
      config._retry = true;
      try {
        await ensureRefreshed();
        return await api.request(config);
      } catch {
        signalUnauthorized();
        return Promise.reject(error);
      }
    }

    if (status === 401) signalUnauthorized();
    return Promise.reject(error);
  },
);

// Build an absolute URL under /api/v1 — only for PUBLIC links that don't need
// the bearer header (e.g. public invoice PDF). Authenticated downloads must go
// through apiDownload() so the Authorization header is attached.
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${V1}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function apiRootUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE_URL) return appUrl(path);
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

function unwrap<T>(data: unknown): T {
  if (data === "" || data == null) return undefined as T;
  if (
    typeof data === "object" &&
    "success" in data &&
    "data" in (data as Record<string, unknown>)
  ) {
    return (data as unknown as { data: T }).data;
  }
  return data as T;
}

function pickMessage(body: unknown): string | null {
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    if (typeof b.message === "string") return b.message;
    if (typeof b.error === "string") return b.error;
    // ASP.NET ProblemDetails validation errors: { errors: { field: [msgs] } }
    if (b.errors && typeof b.errors === "object") {
      const msgs = Object.values(b.errors as Record<string, unknown>)
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .filter((v): v is string => typeof v === "string");
      if (msgs.length) return msgs.join(" ");
    }
    if (typeof b.detail === "string") return b.detail;
    if (typeof b.title === "string") return b.title;
  }
  return null;
}

function pickErrorCode(body: unknown): string | undefined {
  if (body && typeof body === "object") {
    const code = (body as Record<string, unknown>).errorCode;
    if (typeof code === "string") return code;
  }
  return undefined;
}

export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function isNetworkError(e: unknown): boolean {
  return (
    e instanceof ApiError && e.status === 0 && e.errorCode !== "ERR_CANCELED"
  );
}

const OFFLINE_MESSAGE =
  "You're offline. Check your connection and try again.";
const UNREACHABLE_MESSAGE =
  "Can't reach the server right now. Please try again.";
const TIMEOUT_MESSAGE = "The server took too long to respond. Please try again.";

function transportMessage(e: AxiosError): string | null {
  if (e.response) return null;
  if (e.code === "ERR_CANCELED") return null;
  if (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT") return TIMEOUT_MESSAGE;
  return isOffline() ? OFFLINE_MESSAGE : UNREACHABLE_MESSAGE;
}

function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  if (axios.isAxiosError(e)) {
    const status = e.response?.status ?? 0;
    const body = e.response?.data;
    const message =
      pickMessage(body) ??
      transportMessage(e) ??
      e.message ??
      `${status} request failed`;
    const code = pickErrorCode(body) ?? (e.response ? undefined : e.code);
    return new ApiError(status, body, message, code);
  }
  const message = e instanceof Error ? e.message : String(e);
  return new ApiError(0, null, message);
}

// JSON request. Keeps the legacy (path, RequestInit) call shape so feature
// modules only change their paths/DTOs, not their call style.
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  try {
    const res = await api.request({
      url: path,
      method: (init.method ?? "GET") as Method,
      data: init.body as string | undefined,
      headers: {
        "Content-Type": "application/json",
        ...(init.headers as Record<string, string> | undefined),
      },
      signal: init.signal ?? undefined,
    });
    return unwrap<T>(res.data);
  } catch (e) {
    throw toApiError(e);
  }
}

// Multipart upload — never set Content-Type (axios adds the boundary).
export async function apiUpload<T>(
  path: string,
  form: FormData,
  method: "POST" | "PUT" = "POST",
  options: { timeout?: number } = {},
): Promise<T> {
  try {
    const res = await api.request({ url: path, method, data: form, timeout: options.timeout });
    return unwrap<T>(res.data);
  } catch (e) {
    throw toApiError(e);
  }
}

async function blobToApiError(e: unknown): Promise<ApiError> {
  if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
    const text = await e.response.data.text().catch(() => "");
    const body = text ? safeJson(text) : null;
    const status = e.response.status;
    return new ApiError(
      status,
      body,
      pickMessage(body) ?? e.message ?? `${status} request failed`,
      pickErrorCode(body),
    );
  }
  return toApiError(e);
}

// Authenticated binary download (PDF/CSV/document) — attaches the bearer header
// via the instance and returns the Blob; callers turn it into an object URL.
export async function apiDownload(path: string): Promise<Blob> {
  try {
    const res = await api.get(path, { responseType: "blob" });
    return res.data as Blob;
  } catch (e) {
    throw await blobToApiError(e);
  }
}

function filenameFromDisposition(disposition: unknown): string | null {
  if (typeof disposition !== "string") return null;
  const utf8 = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return utf8[1].trim().replace(/^"|"$/g, "");
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() || null;
}

// Same as apiDownload, but also surfaces the server's Content-Disposition
// filename so callers can save the file under the name the API chose.
export async function apiDownloadNamed(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  try {
    const res = await api.get(path, { responseType: "blob" });
    return {
      blob: res.data as Blob,
      filename: filenameFromDisposition(res.headers["content-disposition"]),
    };
  } catch (e) {
    throw await blobToApiError(e);
  }
}

export function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
